import React from 'react'
import { FileText, Clock, Users, Download, Trash2, Eye } from 'lucide-react'
import Button from '../ui/Button'
import { formatFileSize } from '../../utils/formatFileSize'
import { formatDuration, formatRelativeTime, parseInterval } from '../../utils/formatTime'

const TranscriptionCard = ({ 
  transcription, 
  onView, 
  onDownload, 
  onDelete,
  className = '' 
}) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-100'
      case 'processing':
        return 'text-yellow-600 bg-yellow-100'
      case 'failed':
        return 'text-red-600 bg-red-100'
      default:
        return 'text-gray-600 bg-gray-100'
    }
  }

  const getStatusText = (status) => {
    switch (status) {
      case 'completed':
        return '已完成'
      case 'processing':
        return '处理中'
      case 'failed':
        return '处理失败'
      case 'pending':
        return '等待处理'
      default:
        return '未知状态'
    }
  }

  const duration = transcription.duration ? parseInterval(transcription.duration) : 0

  return (
    <div className={`bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow ${className}`}>
      {/* 头部信息 */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start space-x-3">
          <FileText className="h-6 w-6 text-gray-400 mt-1" />
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-medium text-gray-900 truncate">
              {transcription.original_filename}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {formatRelativeTime(transcription.created_at)}
            </p>
          </div>
        </div>

        {/* 状态标签 */}
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(transcription.status)}`}>
          {getStatusText(transcription.status)}
        </span>
      </div>

      {/* 详细信息 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <div className="flex items-center space-x-2">
          <Clock className="h-4 w-4 text-gray-400" />
          <span className="text-sm text-gray-600">
            {duration > 0 ? formatDuration(duration) : '未知'}
          </span>
        </div>

        <div className="flex items-center space-x-2">
          <Users className="h-4 w-4 text-gray-400" />
          <span className="text-sm text-gray-600">
            {transcription.speaker_count || 0} 位说话者
          </span>
        </div>

        <div className="text-sm text-gray-600">
          {formatFileSize(transcription.file_size)}
        </div>

        {transcription.completed_at && (
          <div className="text-sm text-gray-600">
            完成于 {formatRelativeTime(transcription.completed_at)}
          </div>
        )}
      </div>

      {/* 错误信息 */}
      {transcription.status === 'failed' && transcription.error_message && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600">{transcription.error_message}</p>
        </div>
      )}

      {/* 处理中状态 */}
      {transcription.status === 'processing' && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <p className="text-sm text-yellow-600">
            正在处理音频文件，请稍等...这可能需要几分钟时间。
          </p>
        </div>
      )}

      {/* 操作按钮 */}
      <div className="flex items-center justify-end space-x-2">
        {transcription.status === 'completed' && (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onView?.(transcription)}
            >
              <Eye className="h-4 w-4 mr-1" />
              查看
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => onDownload?.(transcription)}
            >
              <Download className="h-4 w-4 mr-1" />
              下载
            </Button>
          </>
        )}

        <Button
          variant="outline"
          size="sm"
          onClick={() => onDelete?.(transcription)}
          className="text-red-600 hover:text-red-700 hover:bg-red-50"
        >
          <Trash2 className="h-4 w-4 mr-1" />
          删除
        </Button>
      </div>
    </div>
  )
}

export default TranscriptionCard